import { useState } from 'react';

import { ReactComponent as AddIcon } from '../../../icons/add.svg';
import { ReactComponent as WarningIcon } from '../../../icons/warning.svg';

import { flattenObject } from '../../../utils/tools';
import { FormModal } from '../../form/modal/FormModal';
import ErrorDialog from '../dialog/error-dialog/ErrorDialog';
import YesNoDialog from '../dialog/yes-no-dialog/YesNoDialog';
import { Table } from '../table/Table';

export const EntityTable = ({
  query,
  getColumnDefinition,
  entityTabs,
  entityName = 'Entity',
  single,
  filters,
  customAction,
  customActionIcon,
  jsonKeys,
  customActionText = 'Action',
  canEdit = true,
  canView = true,
  canDelete = true,
  canExport = true,
  showAction = true
}) => {
  const mergeWithDefaults = (tabs, data = {}) => {
    const defaults = {};
    tabs.forEach((tab) => {
      tab.fields.forEach((field) => {
        const hasValue = data[field.name] !== undefined;
        const hasDefault = field.defaultValue !== undefined;
        if (!hasValue && hasDefault) {
          defaults[field.name] = field.defaultValue;
        }
      });
    });
    return { ...defaults, ...data };
  };

  const {
    data: entities,
    loading,
    error,
    create,
    update,
    remove,
    mutating: processing,
    getById,
    reset
  } = query;

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [display, setDisplay] = useState(false);
  const [formData, setFormData] = useState({});

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogText, setErrorDialogText] = useState('Unknown Error');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogText, setDeleteDialogText] = useState('');
  const [deleteId, setDeleteId] = useState();

  const handleEditClick = async (item, displayMode = false) => {
    reset();
    const entity = entities.find((e) => e.id === item.id);
    if (!entity) return;
    setOpen(true);

    await getById(entity.id, (result) => {
      let mergedData = mergeWithDefaults(entityTabs, result);
      if (jsonKeys) {
        jsonKeys.forEach((key) => {
          mergedData = flattenObject(mergedData, key);
        });
      }
      setFormData(mergedData);
      setEditId(item.id);
      setDisplay(displayMode);
    });
  };

  const showErrorDialog = (message) => {
    setErrorDialogText(message);
    setErrorDialogOpen(true);
  };

  const handleViewClick = async (item) => {
    reset();
    await handleEditClick(item, true);
  };

  const handleCreateClick = () => {
    reset();
    setEditId(null);

    const mergedDefaults = mergeWithDefaults(entityTabs, {});
    setFormData(mergedDefaults);

    setOpen(true);
    setDisplay(false);
  };

  const handleSave = (data) => {
    create({
      data,
      onSuccess: () => setOpen(false),
      //eslint-disable-next-line
      onError: (err) => console.log(err)
    });
  };

  const handleUpdate = (id, data) => {
    update({
      data,
      onSuccess: () => setOpen(false),
      onError: () =>
        showErrorDialog(
          `There was an error updating the ${entityName.toLowerCase()}. Please try again!`
        )
    });
  };

  const showDeleteDialog = (entity) => {
    setDeleteDialogText(
      `Are you sure you want to permanently delete the ${entityName.toLowerCase()} '
            <strong>${entity.name || entity.title || entity.id}</strong>'? <br /><br />
      This action cannot be undone.`
    );
    setDeleteId(entity.id);
    setDeleteDialogOpen(true);
  };

  const handleOnDelete = () => {
    remove({
      data: deleteId,
      onSuccess: () => setDeleteDialogOpen(false),
      onComplete: () => {
        setDeleteDialogOpen(false);
        setDeleteId(null);
      },
      onError: () =>
        showErrorDialog(
          `There was an error deleting the ${entityName.toLowerCase()}. Please try again!`
        )
    });
  };

  return (
    <>
      <FormModal
        isOpen={open && !errorDialogOpen}
        onClose={() => {
          setOpen(false);
          setEditId(null);
          setFormData({});
        }}
        single={single}
        display={display}
        title={
          editId
            ? display
              ? `View ${entityName}`
              : `Edit ${entityName}`
            : processing
              ? entityName
              : `Create ${entityName}`
        }
        isEdit={!!editId}
        itemId={editId}
        tabs={entityTabs}
        formData={formData}
        saveItem={handleSave}
        updateItem={handleUpdate}
        loading={processing}
      />

      <ErrorDialog
        isOpen={errorDialogOpen}
        message={errorDialogText}
        onClose={() => setErrorDialogOpen(false)}
        onClick={() => {
          reset();
          setErrorDialogOpen(false);
        }}
      />
      <YesNoDialog
        icon={<WarningIcon width={56} height={56} style={{ color: '#ff9800' }} />}
        title={`Delete ${entityName}`}
        isOpen={deleteDialogOpen}
        message={deleteDialogText}
        onYesClick={handleOnDelete}
        onNoClick={() => setDeleteDialogOpen(false)}
        onClose={() => setDeleteDialogOpen(false)}
        loading={processing}
      />
      <Table
        id={entityName.toLowerCase() + 's'}
        columns={getColumnDefinition(
          canEdit ? handleEditClick : undefined,
          canView ? handleViewClick : undefined,
          canDelete ? showDeleteDialog : undefined,
          customAction,
          customActionText,
          customActionIcon
        )}
        data={entities ?? []}
        action={showAction ? 'Create' : null}
        actionIcon={AddIcon}
        onActionClick={handleCreateClick}
        loading={loading}
        error={error}
        filters={filters}
        canExport={canExport}
      />
    </>
  );
};
